import type {
  CustomerInput,
  DocumentInput,
  LicenceCatalog,
  LicenceDownloadRow,
  LicenceInput,
  LicenceIssueOutcome,
  PriceListResponse,
  QuoteCatalogResponse,
  ResellerBranding,
  ResellerCustomer,
  ResellerCustomerDetail,
  ResellerDocumentDetail,
  ResellerInvoice,
  ResellerLicence,
  ResellerOverview,
} from "@xop-any-time/shared";

import { api } from "./api.js";
import { env } from "./env.js";

/**
 * Every call the portal makes, in one typed place.
 *
 * Components import these rather than assembling paths inline, so a route
 * rename is one edit and no component can accidentally call an endpoint the
 * BFF does not expose. The query-key helpers live here too, so an invalidation
 * after a write and the fetch it invalidates cannot drift apart — the usual
 * way a saved edit fails to appear until reload.
 */

export interface Pagination {
  page: number;
  perPage: number;
  totalPages: number;
  totalCount: number;
}

export type SortDir = "asc" | "desc";

/**
 * `| undefined` explicitly, not just `?`. Under
 * `exactOptionalPropertyTypes` a `?` property may be ABSENT but may not HOLD
 * `undefined`, and every caller here builds this object from state that is
 * legitimately undefined ("no search", "no sort yet"). Spreading those away
 * conditionally at each of the four call sites would be noise; saying the
 * type allows undefined is the honest description.
 */
export interface ListParams {
  page?: number | undefined;
  search?: string | undefined;
  sortBy?: string | undefined;
  sortDir?: SortDir | undefined;
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/* ── Query keys ──────────────────────────────────────────────────────── */

export const keys = {
  overview: ["overview"] as const,
  branding: ["branding"] as const,
  customers: (p: ListParams) => ["customers", p] as const,
  customer: (id: number) => ["customer", id] as const,
  documents: (type: "invoice" | "quote", p: ListParams) => ["documents", type, p] as const,
  document: (id: number) => ["document", id] as const,
  licences: (p: { expiring?: boolean; customerId?: number } = {}) => ["licences", p] as const,
  licence: (id: number) => ["licence", id] as const,
  licenceCatalog: ["licence-catalog"] as const,
  licenceDownloads: (id: number) => ["licence-downloads", id] as const,
  licenceTrialAvailability: (customerId: number) => ["licence-trial-availability", customerId] as const,
  priceList: ["price-list"] as const,
};

/* ── Reads ───────────────────────────────────────────────────────────── */

export const getOverview = (): Promise<ResellerOverview> =>
  api<ResellerOverview>("/api/crm/overview");

export const getBranding = (): Promise<{ branding: ResellerBranding }> =>
  api<{ branding: ResellerBranding }>("/api/crm/branding");

/** The catalogue behind the quote REQUEST form — no prices in the payload. */
export const getQuoteCatalog = (): Promise<QuoteCatalogResponse> =>
  api<QuoteCatalogResponse>("/api/crm/quote-catalog");

export const listCustomers = (
  p: ListParams,
): Promise<{ customers: ResellerCustomer[]; pagination: Pagination }> =>
  api(
    `/api/crm/customers${query({
      page: p.page,
      per_page: 25,
      search: p.search,
      sort_by: p.sortBy,
      sort_dir: p.sortDir,
    })}`,
  );

export const getCustomer = (id: number): Promise<{ customer: ResellerCustomerDetail }> =>
  api(`/api/crm/customers/${id}`);

export const listDocuments = (
  type: "invoice" | "quote",
  p: ListParams,
): Promise<{ invoices: ResellerInvoice[]; pagination: Pagination }> =>
  api(
    `/api/crm/documents${query({
      page: p.page,
      per_page: 25,
      document_type: type,
      sort_by: p.sortBy,
      sort_dir: p.sortDir,
    })}`,
  );

export const getDocument = (id: number): Promise<{ document: ResellerDocumentDetail }> =>
  api(`/api/crm/documents/${id}`);

/* ── Licensing ───────────────────────────────────────────────────────── */

/**
 * Every licence in the reseller's subtree, soonest expiry first, perpetual last.
 *
 * ONE endpoint behind two surfaces — the licences page and the overview's
 * "expires next" — so the front page cannot disagree with the list about what
 * lapses next.
 *
 * A licence is ONE APPLIANCE now, so a customer may appear more than once.
 */
export const listLicences = (
  p: { expiring?: boolean; customerId?: number } = {},
): Promise<{ licences: ResellerLicence[] }> =>
  api(
    `/api/crm/licences${query({
      ...(p.expiring ? { expiring: "true" } : {}),
      ...(p.customerId ? { customerId: p.customerId } : {}),
    })}`,
  );

export const getLicence = (id: number): Promise<{ licence: ResellerLicence }> =>
  api(`/api/crm/licences/${id}`);

/** The Pricing features a licence may carry — each naming the appliance key
 * it switches on, or none while XOP has not linked it — with the appliance's
 * own 76 keys beside them. */
export const getLicenceCatalog = (): Promise<LicenceCatalog> =>
  api("/api/crm/licences/catalog");

export const createLicence = (body: LicenceInput & { customer_id: number }): Promise<{ licence: ResellerLicence }> =>
  api("/api/crm/licences", { method: "POST", body });

export const saveLicence = (id: number, body: LicenceInput): Promise<{ licence: ResellerLicence }> =>
  api(`/api/crm/licences/${id}`, { method: "PATCH", body });

/**
 * May this customer still get a 30-day TRIAL licence? True only while it holds
 * no other licence. The builder asks before a NEW licence exists; an existing
 * licence carries the same answer as `trial.available`.
 */
export const getTrialAvailability = (customerId: number): Promise<{ available: boolean }> =>
  api(`/api/crm/licences/trial-availability${query({ customerId })}`);

/** Remove an UNISSUED licence — the rollback behind one-press Generate. */
export const deleteLicence = (id: number): Promise<void> =>
  api(`/api/crm/licences/${id}`, { method: "DELETE" });

/**
 * Mint a NEW signed bundle. Supersedes the previous issue and starts a fresh
 * download count.
 *
 * The signing happens in crm-api — this app holds no licence key material.
 * `warnings` carries anything the validator allowed but wanted said out loud.
 */
export const issueLicence = (
  id: number,
): Promise<{
  licence: ResellerLicence;
  /** "issued" means a file exists; "approval_requested" means NOTHING was signed
   * and XOP's executives have been emailed. Never assume Issue produced a file. */
  outcome: LicenceIssueOutcome;
  warnings: Array<{ code: string; message: string }>;
}> => api(`/api/crm/licences/${id}/issue`, { method: "POST" });

/**
 * Draft this licence's SUCCESSOR — a new full licence for the same appliance,
 * same features and ports, to be given a term and created like any other.
 * Allowed from 30 days before expiry; crm-api refuses everything else with a
 * sentence (`LICENCE_RENEWAL_TOO_EARLY`, `LICENCE_RENEWAL_EXISTS`, …) that the
 * page shows as it came.
 */
export const renewLicence = (
  id: number,
  body: { expiresOn?: string | null } = {},
): Promise<{ licence: ResellerLicence }> =>
  api(`/api/crm/licences/${id}/renew`, { method: "POST", body });

/** Every pull of this licence's file, newest first, refusals included. */
export const getLicenceDownloads = (id: number): Promise<{ downloads: LicenceDownloadRow[] }> =>
  api(`/api/crm/licences/${id}/downloads`);

/**
 * The signed licence file, as bytes.
 *
 * Fetched rather than linked so a refusal surfaces as a sentence in the page
 * instead of a browser tab full of JSON. That matters most for the download
 * cap: "Ask an administrator to raise the limit" is actionable, a raw 403 body
 * is not.
 */
export const downloadLicenceFile = async (id: number): Promise<Blob> => {
  const res = await fetch(`${env.apiUrl}/api/crm/licences/${id}/file`, {
    credentials: "include",
  });
  if (!res.ok) {
    let message = `Could not download the licence file (${res.status}).`;
    try {
      message = ((await res.json()) as { message?: string }).message ?? message;
    } catch {
      // Non-JSON body (a proxy error page) — keep the generic message.
    }
    throw new Error(message);
  }
  return res.blob();
};

/* ── Writes ──────────────────────────────────────────────────────────── */

export const createCustomer = (body: CustomerInput): Promise<{ customer: ResellerCustomer }> =>
  api("/api/crm/customers", { method: "POST", body });

export const updateCustomer = (
  id: number,
  body: CustomerInput,
): Promise<{ customer: ResellerCustomer }> =>
  api(`/api/crm/customers/${id}`, { method: "PATCH", body });

export const createDocument = (
  body: DocumentInput,
): Promise<{ document: ResellerDocumentDetail }> =>
  api("/api/crm/documents", { method: "POST", body });

export const updateDocument = (
  id: number,
  body: DocumentInput,
): Promise<{ document: ResellerDocumentDetail }> =>
  api(`/api/crm/documents/${id}`, { method: "PATCH", body });

export const deleteDocument = (id: number): Promise<void> =>
  api(`/api/crm/documents/${id}`, { method: "DELETE" });

/** Where the browser fetches the logo image from. Same-origin through the
 * BFF, so the `<img>` carries the session cookie. */
export const LOGO_URL = "/api/crm/branding/logo";

/* ── The price list ──────────────────────────────────────────────────── */

/**
 * The XOP Networks price list a reseller quotes from (spec §6).
 *
 * ONE list behind the quote builder and the invoice builder both, cached for
 * 60 s in the BFF rather than here: a react-query cache would be per tab, and
 * the thing worth not repeating is the crm-api round trip.
 */
export const getPriceList = (): Promise<PriceListResponse> =>
  api<PriceListResponse>("/api/crm/price-list");
